import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { ArrowLeft, Hash } from 'lucide-react';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const regId = sp.get('registration_id') || '';
  const emailMasked = sp.get('email_masked') || '';
  const phoneMasked = sp.get('phone_masked') || '';
  const simulated = sp.get('simulated') || '0';
  const initialDevCode = sp.get('dev_code') || '';

  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState(initialDevCode);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timer.current = setTimeout(() => setCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer.current);
  }, [cooldown]);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (code.length < 6) { setError('Enter the 6-digit code we sent to your email.'); return; }
    setSubmitting(true);
    try {
      const body = await apiFetch('/api/v1/auth/email/verify', {
        method: 'POST', auth: false,
        body: JSON.stringify({ registration_id: regId, code }),
      });
      const params = new URLSearchParams({
        registration_id: body.registration_id,
        phone_masked: phoneMasked,
        dev_code: body.dev_code_phone || '',
        simulated,
      });
      navigate(`/verify-phone?${params.toString()}`, { replace: true });
    } catch (err) {
      if (err.code === 'RATE_LIMITED') {
        setError(`Too many attempts. Try again in ${err.details?.retry_after_seconds ?? 'a few'}s.`);
      } else {
        setError(err.message || 'Invalid code.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function resend() {
    setError(null);
    setCooldown(30);
    try {
      const body = await apiFetch('/api/v1/auth/email/resend', {
        method: 'POST', auth: false,
        body: JSON.stringify({ registration_id: regId }),
      });
      if (body.dev_code_email) setDevCode(body.dev_code_email);
    } catch (err) {
      if (err.code === 'RATE_LIMITED') {
        setError(`Please wait ${err.details?.retry_after_seconds ?? 'a few'}s before requesting again.`);
      } else { setError(err.message || 'Could not resend code.'); }
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="verify-email-screen">
      <div className="w-full max-w-md">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> back
        </button>

        <div className="mb-1 flex items-center gap-2">
          <img src="/allviews-mark.svg" alt="" className="h-5 w-5" />
          <img src="/allviews-wordmark.svg" alt="ALLVIEWS" className="h-3.5" />
          <span className="text-xs text-[#5B6472] ml-1">Verify Email</span>
        </div>

        <div className="flex items-center justify-between mt-6 mb-2">
          <div>
            <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-1" data-testid="screen-title">Verify your email</h1>
            <p className="text-sm text-[#5B6472]">
              We sent a 6-digit code to <span className="font-bold text-[#0B1B46]">{emailMasked}</span>. Enter it below.
            </p>
          </div>
          <span className="bg-[#EFF5FF] text-[#146BFF] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Step 2 of 3</span>
        </div>

        <div className="flex gap-2 mb-6" data-testid="verify-email-progress">
          <div className="h-1.5 flex-1 rounded-full bg-[#146BFF]" />
          <div className="h-1.5 flex-1 rounded-full bg-[#146BFF]" />
          <div className="h-1.5 flex-1 rounded-full bg-[#E3E8EF]" />
        </div>

        {devCode && (
          <div className="bg-[#FBEEDA] border border-[#B86500]/20 rounded-lg p-3 mb-4" data-testid="dev-code-banner-email">
            <p className="text-xs font-bold text-[#B86500] mb-0.5">DEV — Simulated email code</p>
            <p className="text-lg font-mono font-bold text-[#0B1B46]" data-testid="dev-code-banner-email-code">{devCode}</p>
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="verify-email-code" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Email verification code</label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
              <input
                id="verify-email-code" maxLength={6} placeholder="6-digit code"
                value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full h-11 pl-10 pr-3 border border-[#E3E8EF] rounded-lg text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white"
                data-testid="verify-email-code"
              />
            </div>
          </div>

          {error && (
            <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="verify-email-error">{error}</div>
          )}

          <button type="submit" disabled={submitting}
            className="h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
            data-testid="verify-email-submit-btn">
            {submitting ? 'Verifying…' : 'Verify email'}
          </button>

          <button type="button" onClick={resend} disabled={cooldown > 0}
            className="h-11 text-[#146BFF] font-semibold rounded-lg hover:bg-[#EFF5FF] disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
            data-testid="verify-email-resend-btn">
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
          </button>
        </form>
      </div>
    </div>
  );
}
